/**
 * QuotationDetailPage — detail view of a single quotation.
 *
 * Features:
 * - Full quotation info display
 * - Status badge + action buttons (state machine)
 * - "Convertir a Cliente" button visible only when: status = 'accepted' AND no client_id
 * - "Cotizacion Convertida" badge with link to client when already converted
 * - "Descargar Contrato" link when contractPdfUrl exists after conversion
 * - Confirmation dialog before converting
 * - On successful conversion: navigate to /clients/:newClientId
 * - Timeline of status changes (sent_at, accepted_at, rejected_at)
 * - Back navigation to list
 */

import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuotation, useAdvanceQuotationStatus, useConvertQuotation } from '@/hooks/useQuotations'
import {
  QuotationStatusBadge,
  QuotationStatusActions,
} from '@/components/quotations/QuotationStatusActions'
import type { QuotationStatus } from '@1klick/types'

function formatCents(cents: number): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  }).format(cents / 100)
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

interface TimelineEvent {
  label: string
  date: string | null
  color: string
}

export function QuotationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: quotation, isLoading, isError } = useQuotation(id ?? '')
  const advanceMutation = useAdvanceQuotationStatus()
  const convertMutation = useConvertQuotation()

  // State for the convert confirmation dialog
  const [showConvertDialog, setShowConvertDialog] = useState(false)
  // Store the contract PDF URL received after successful conversion
  const [contractPdfUrl, setContractPdfUrl] = useState<string | null>(null)

  const handleAdvance = (newStatus: QuotationStatus) => {
    if (!id) return
    advanceMutation.mutate({ id, status: newStatus })
  }

  const handleConvertClick = () => {
    setShowConvertDialog(true)
  }

  const handleConvertConfirm = () => {
    if (!id) return
    setShowConvertDialog(false)
    convertMutation.mutate(id, {
      onSuccess: (data) => {
        // Store the contract PDF URL for download
        if (data.contractPdfUrl) {
          setContractPdfUrl(data.contractPdfUrl)
        }
        // Navigate to the newly created client
        navigate(`/clients/${data.client.id}`)
      },
    })
  }

  const handleConvertCancel = () => {
    setShowConvertDialog(false)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500">Cargando cotizacion...</p>
      </div>
    )
  }

  if (isError || !quotation) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-red-500">Error al cargar la cotizacion</p>
      </div>
    )
  }

  // Build timeline events
  const timeline: TimelineEvent[] = [
    { label: 'Creada', date: quotation.created_at, color: 'bg-gray-400' },
  ]
  if (quotation.sent_at) {
    timeline.push({ label: 'Enviada', date: quotation.sent_at, color: 'bg-blue-400' })
  }
  if (quotation.accepted_at) {
    timeline.push({ label: 'Aceptada', date: quotation.accepted_at, color: 'bg-green-400' })
  }
  if (quotation.rejected_at) {
    timeline.push({ label: 'Rechazada', date: quotation.rejected_at, color: 'bg-red-400' })
  }
  if (quotation.client_id) {
    timeline.push({ label: 'Convertida a Cliente', date: quotation.accepted_at, color: 'bg-purple-400' })
  }

  // Determine convert button visibility:
  // Show when status = 'accepted' AND no client_id (not yet converted)
  const canConvert = quotation.status === 'accepted' && !quotation.client_id
  const isConverted = quotation.status === 'accepted' && !!quotation.client_id

  return (
    <div>
      {/* Convert Confirmation Dialog */}
      {showConvertDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              Confirmar conversion a cliente
            </h3>
            <p className="mb-4 text-sm text-gray-600">
              Se creara un nuevo Cliente y una Cuenta a partir de esta cotizacion. Esta accion
              no se puede deshacer.
            </p>
            <p className="mb-6 text-sm font-medium text-gray-800">
              Cliente: <span className="text-blue-700">{quotation.client_name}</span>
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleConvertCancel}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleConvertConfirm}
                className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
              >
                Confirmar conversion
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <Link
          to="/quotations"
          className="mb-2 inline-block text-sm text-blue-600 hover:text-blue-800"
        >
          &larr; Volver a cotizaciones
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{quotation.client_name}</h1>
            <QuotationStatusBadge status={quotation.status} />
            {isConverted && (
              <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                Convertida
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <QuotationStatusActions
              status={quotation.status}
              onAdvance={handleAdvance}
              isAdvancing={advanceMutation.isPending}
            />
            {/* Convert to Client button — only for accepted + unconverted quotations */}
            {canConvert && (
              <button
                onClick={handleConvertClick}
                disabled={convertMutation.isPending}
                className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {convertMutation.isPending ? 'Convirtiendo...' : 'Convertir a Cliente'}
              </button>
            )}
            {/* Link to client if already converted */}
            {isConverted && quotation.client_id && (
              <Link
                to={`/clients/${quotation.client_id}`}
                className="rounded-md border border-purple-300 bg-purple-50 px-4 py-2 text-sm font-medium text-purple-700 hover:bg-purple-100"
              >
                Ver Cliente
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Error banners */}
      {advanceMutation.isError && (
        <div className="mb-4 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">
            Error: {advanceMutation.error?.message}
          </p>
        </div>
      )}
      {convertMutation.isError && (
        <div className="mb-4 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">
            Error al convertir: {convertMutation.error?.message}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main info */}
        <div className="lg:col-span-2">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Informacion de la cotizacion</h2>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Nombre del cliente</dt>
                <dd className="mt-1 text-sm text-gray-900">{quotation.client_name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Email de contacto</dt>
                <dd className="mt-1 text-sm text-gray-900">{quotation.contact_email}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Telefono</dt>
                <dd className="mt-1 text-sm text-gray-900">{quotation.contact_phone ?? '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Valida hasta</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatDate(quotation.valid_until)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Total</dt>
                <dd className="mt-1 text-lg font-semibold text-gray-900">
                  {formatCents(quotation.total_cents)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Descuento</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatCents(quotation.discount_cents)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Neto</dt>
                <dd className="mt-1 text-lg font-semibold text-green-700">
                  {formatCents(quotation.total_cents - quotation.discount_cents)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Creada por</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {quotation.creator_name ?? '-'}
                  {quotation.creator_email && (
                    <span className="ml-1 text-gray-500">({quotation.creator_email})</span>
                  )}
                </dd>
              </div>
              {isConverted && quotation.client_id && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Cliente creado</dt>
                  <dd className="mt-1 text-sm text-purple-700">
                    <Link
                      to={`/clients/${quotation.client_id}`}
                      className="underline hover:text-purple-900"
                    >
                      Ver perfil del cliente
                    </Link>
                  </dd>
                </div>
              )}
            </dl>

            {quotation.notes && (
              <div className="mt-6 border-t border-gray-200 pt-4">
                <dt className="text-sm font-medium text-gray-500">Notas</dt>
                <dd className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
                  {quotation.notes}
                </dd>
              </div>
            )}

            {/* Contract download link */}
            {contractPdfUrl && (
              <div className="mt-6 border-t border-gray-200 pt-4">
                <a
                  href={`/api/v1/quotations/${quotation.id}/contract`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
                >
                  Descargar Contrato PDF
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Timeline sidebar */}
        <div>
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Historial</h2>
            <div className="space-y-4">
              {timeline.map((event, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className={`mt-1 h-3 w-3 rounded-full ${event.color}`} />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{event.label}</p>
                    <p className="text-xs text-gray-500">{formatDateTime(event.date)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Metadata */}
          <div className="mt-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Metadatos</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-xs font-medium text-gray-500">ID</dt>
                <dd className="mt-0.5 text-xs font-mono text-gray-700">{quotation.id}</dd>
              </div>
              {quotation.client_id && (
                <div>
                  <dt className="text-xs font-medium text-gray-500">ID de Cliente</dt>
                  <dd className="mt-0.5 text-xs font-mono text-gray-700">{quotation.client_id}</dd>
                </div>
              )}
              <div>
                <dt className="text-xs font-medium text-gray-500">Creada</dt>
                <dd className="mt-0.5 text-xs text-gray-700">{formatDateTime(quotation.created_at)}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Actualizada</dt>
                <dd className="mt-0.5 text-xs text-gray-700">{formatDateTime(quotation.updated_at)}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  )
}
